"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { ChevronDown, ChevronUp } from "lucide-react";

interface Picture {
  data: {
    id: string;
    attributes: {
      url: string;
      name: string;
      alternativeText: string;
    };
  };
}

interface TabContent {
  id: number;
  heading: string;
  description?: string;
  picture?: Picture;
}

interface Props {
  data: {
    title?: string;
    tabContent: TabContent[];
  };
}

// --- UPDATED MARKDOWN STYLES ---
const markdownComponents = {
  p: ({ children }: any) => (
    <p className="text-gray-600 text-xs md:text-sm lg:text-base leading-relaxed mb-4 pl-6 last:mb-0">
      {children}
    </p>
  ),
  strong: ({ children }: any) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),
  ul: ({ children }: any) => (
    <ul className="list-disc list-outside text-xs md:text-sm lg:text-base pl-10 mb-4 text-gray-600 space-y-1 marker:text-indigo-600">
      {children}
    </ul>
  ),
  // Updated OL: Pass props to handle 'start' attributes correctly
  ol: ({ children, ...props }: any) => (
    <ol {...props} className="list-outside ml-5 mb-4 text-black text-sm md:text-xl font-semibold space-y-1 marker:text-orange-500">
      {children}
    </ol>
  ),
  li: ({ children }: any) => <li className="pl-1">{children}</li>,
  a: ({ href, children }: any) => (
    <a href={href} className="text-red-500 hover:underline font-medium leading-relaxed">
      {children}
    </a>
  ),
  blockquote: ({ children }: any) => (
    <blockquote className="border-l-4 border-gray-200 pl-4 italic text-gray-500 my-4">
      {children}
    </blockquote>
  ),
};

export default function DataDisplayTabs_Layout2({ data }: Props) {
  const [activeId, setActiveId] = useState<number | null>(data.tabContent[0]?.id || null);

  const toggleTab = (id: number) => {
    setActiveId(activeId === id ? null : id);
  };

  return (
    <section className="relative w-full py-8 bg-gradient-to-b from-[#F472B6]/10 via-[#F0F7FF] to-[#F472B6]/10">
      <div className="max-w-6xl mx-auto px-6">
        
        {/* TITLE */}
        {data.title && (
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight"
            >
              {data.title}
            </motion.h2>
            <div className="h-1 w-24 bg-red-500 mx-auto mt-4 rounded-full" />
          </div>
        )}

        {/* ACCORDION LIST */}
        <div className="flex flex-col space-y-4">
          {data.tabContent.map((item) => {
            const isOpen = activeId === item.id;
            const imageUrl = item.picture?.data?.attributes?.url;

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className={`bg-white rounded-2xl overflow-hidden border transition-all duration-300 ${
                  isOpen ? "border-gray-300 shadow-lg" : "border-gray-200 shadow-sm hover:border-gray-300"
                }`}
              >
                {/* HEADER */}
                <button
                  onClick={() => toggleTab(item.id)}
                  className="w-full flex items-center justify-between p-6 text-left focus:outline-none group"
                >
                  <span className={`text-sm md:text-xl font-semibold transition-colors ${
                    isOpen ? "text-sky-600" : "text-gray-900 group-hover:text-sky-600"
                  }`}>
                    {item.heading}
                  </span>
                  <span className={`ml-4 p-2 rounded-full transition-colors ${
                    isOpen ? "bg-sky-50 text-sky-600" : "bg-gray-100 text-gray-500 group-hover:bg-sky-50 group-hover:text-sky-600"
                  }`}>
                    {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </span>
                </button>

                {/* BODY */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <div className="border-t border-gray-100 p-6 pt-0">
                        <div className="flex flex-col md:flex-row gap-8 mt-6">
                          
                          {imageUrl && (
                            <div className="w-full md:w-1/3 flex-shrink-0">
                              <div className="relative aspect-[4/3] md:aspect-[3/4] rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                                <img
                                  src={imageUrl}
                                  alt={item.heading}
                                  className="absolute inset-0 w-full h-full object-cover"
                                />
                              </div>
                            </div>
                          )}

                          {/* Markdown Wrapper with Nested List Styles */}
                          <div className="w-full md:w-2/3">
                            <div className="
                              [&_ol]:list-decimal 
                              [&_ol_ol]:list-[lower-alpha] 
                              [&_ol_ol_ol]:list-[lower-roman]
                            ">
                              {item.description ? (
                                <ReactMarkdown 
                                  remarkPlugins={[remarkGfm, remarkBreaks]}
                                  components={markdownComponents}
                                >
                                  {item.description}
                                </ReactMarkdown>
                              ) : (
                                <p className="text-gray-400 italic">No description available.</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}